// src/hooks/useUpload.js - state of one file upload (audit FL-09, FL-10, FL-39).
//
//   const upload = useUpload({ label: 'Hudba' });
//   upload.start(file, (onProgress, signal) => uploadFile(path, file, { onProgress, signal }));
//   upload.status: idle | uploading | processing | success | error | cancelled
//
// * progress, bytes, speed and remaining time for the UI;
// * cancel() aborts the request (reported as "cancelled", not as an error);
// * a newer start() supersedes an older one, whose late result is ignored;
// * the upload is registered with the UploadProvider (navbar indicator, leave-page
//   guard, wake lock) and keeps running if the page is left - the result is then
//   reported as a toast. Without a provider the hook guards the page itself.
import { useCallback, useEffect, useRef, useState } from 'react';
import { getErrorMessage } from '../services/api';
import { useToast } from '../components/ui/Toast';
import { useUploadManager } from '../components/leader/UploadManager';
import useUploadGuard from './useUploadGuard';

const IDLE = {
  status: 'idle',
  progress: 0,
  loaded: 0,
  total: 0,
  fileName: '',
  error: null,
  rate: null,
  eta: null,
};

let sequence = 0;

export default function useUpload({ label } = {}) {
  const manager = useUploadManager();
  const toast = useToast();
  const [state, setState] = useState(IDLE);
  const controllerRef = useRef(null);
  const mountedRef = useRef(true);
  const busy = state.status === 'uploading' || state.status === 'processing';

  useUploadGuard(busy && !manager);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const start = useCallback(
    (file, task) => {
      if (controllerRef.current) controllerRef.current.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const id = `upload-${++sequence}`;
      const startedAt = Date.now();
      const total = file.size || 0;
      const fileName = file.name;

      setState({ ...IDLE, status: 'uploading', total, fileName });
      if (manager) manager.update(id, { fileName, label, progress: 0, status: 'uploading' });

      let lastPercent = -1;
      const onProgress = (percent, info = {}) => {
        if (controllerRef.current !== controller) return;
        const progress = Math.max(0, Math.min(100, Math.round(Number(percent) || 0)));
        const loaded = typeof info.loaded === 'number' ? info.loaded : (total * progress) / 100;
        const elapsed = (Date.now() - startedAt) / 1000;
        const rate = info.rate || (elapsed >= 2 && loaded > 0 ? loaded / elapsed : null);
        const eta = typeof info.estimated === 'number' ? info.estimated : rate ? (total - loaded) / rate : null;
        const status = progress >= 100 ? 'processing' : 'uploading';
        setState((s) => ({ ...s, status, progress, loaded, rate, eta }));
        if (manager && progress !== lastPercent) {
          lastPercent = progress;
          manager.update(id, { progress, status });
        }
      };

      const settle = () => {
        const current = controllerRef.current === controller;
        if (current) controllerRef.current = null;
        if (manager) manager.remove(id);
        return current;
      };

      let promise;
      try {
        promise = Promise.resolve(task(onProgress, controller.signal));
      } catch (err) {
        promise = Promise.reject(err);
      }

      return promise.then(
        (result) => {
          if (settle()) {
            setState((s) => ({ ...s, status: 'success', progress: 100, error: null, eta: null }));
            if (!mountedRef.current) toast.success(`Soubor „${fileName}“ byl nahrán.`);
          }
          return result;
        },
        (err) => {
          if (!settle()) return undefined; // superseded by a newer upload
          if (controller.signal.aborted || (err && err.cancelled)) {
            setState((s) => ({ ...s, status: 'cancelled', error: null }));
            return undefined;
          }
          const message = getErrorMessage(err);
          setState((s) => ({ ...s, status: 'error', error: message }));
          if (!mountedRef.current) toast.error(`Soubor „${fileName}“ se nepodařilo nahrát. ${message}`);
          return undefined;
        }
      );
    },
    [manager, toast, label]
  );

  const cancel = useCallback(() => {
    if (controllerRef.current) controllerRef.current.abort();
  }, []);

  /** Show a validation problem found before uploading (wrong type, too big...). */
  const fail = useCallback((message, fileName = '') => {
    setState({ ...IDLE, status: 'error', error: message, fileName });
  }, []);

  const reset = useCallback(() => setState(IDLE), []);

  return { ...state, busy, start, cancel, fail, reset };
}
